import { Form, Formik, FormikHelpers } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { TypeableDatePicker } from "@volca/ui/components/ui/typeable-date-picker";
import { toast } from "@volca/ui/lib/toast";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { dayjs } from "@/core/utils/date";
import { useUpsertReferralSource } from "@/referral-sources/query";
import { ReferralSource } from "@/referral-sources/referral-source";

interface ReferralSourceFormProps {
  mode: "view" | "new";
  referralSource: ReferralSource | null | undefined;
  businessId: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  description: Yup.string().optional(),
  start_date: Yup.date().required("Start date is required"),
  end_date: Yup.date()
    .optional()
    .nullable()
    .test({
      name: "end-date-follows-start-date",
      message: "End date must follow start date",
      test: (endDate, context) => {
        const startDate = context?.parent?.start_date;
        const result = startDate && endDate ? endDate > startDate : true;
        return result;
      },
    }),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const ReferralSourceForm = ({
  mode,
  referralSource,
  businessId,
}: ReferralSourceFormProps) => {
  const isNewMode = mode === SIDEBAR_MODES.new;

  const initialValues = useMemo<FormValues>(() => {
    const startDate = referralSource?.start_date
      ? dayjs(referralSource.start_date).toDate()
      : dayjs().toDate();
    const endDate = referralSource?.end_date
      ? dayjs(referralSource.end_date).toDate()
      : undefined;

    return {
      name: referralSource?.name ?? "",
      description: referralSource?.description ?? "",
      start_date: startDate,
      end_date: endDate,
    };
  }, [referralSource]);

  const { mutate: upsertReferralSource } = useUpsertReferralSource(businessId);

  const { updateSidebar } = useBusinessSettingsSidebar();

  const handleSubmit = async (
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>,
  ) => {
    const {
      start_date: startDateObj,
      end_date: endDateObj,
      ...otherValues
    } = values;

    upsertReferralSource(
      {
        ...otherValues,
        start_date: startDateObj.toISOString(),
        end_date: endDateObj?.toISOString() ?? null,
        id: referralSource?.id,
        business_id: businessId,
      },
      {
        onSuccess: (response) => {
          const newRecord = response.data;
          resetForm({ values });

          updateSidebar({
            mode: SIDEBAR_MODES.view,
            selectedItemId: newRecord.id,
          });

          toast.success(
            `Referral source ${isNewMode ? "created" : "updated"} successfully`,
          );
        },
        onError: (error) => {
          toast.error(
            `Failed to ${isNewMode ? "create" : "update"} referral source`,
            {
              description:
                error instanceof Error ? error.message : "Please try again",
            },
          );
        },
      },
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ getFieldProps, dirty, resetForm, setFieldValue, isValid }) => (
        <Form>
          <FormInput
            label="Name"
            placeholder="Enter name"
            required
            {...getFieldProps("name")}
            fullWidth
            className="mb-6"
          />

          <label className="mb-6 block">
            <Text variant="label" tone="muted" className="mb-2">
              Description
            </Text>

            <Textarea
              className="min-h-32"
              placeholder="Enter description"
              {...getFieldProps("description")}
            />
          </label>

          <div className="mb-6">
            <TypeableDatePicker
              label="Start date"
              required
              {...getFieldProps("start_date")}
              onChange={(date) => setFieldValue("start_date", date)}
              fullWidth
            />
          </div>

          <div className="mb-6">
            <TypeableDatePicker
              label="End date"
              {...getFieldProps("end_date")}
              onChange={(date) => setFieldValue("end_date", date)}
              fullWidth
            />
          </div>

          <Stack align="start" gap={4} direction="row">
            <Button type="submit" size="sm" disabled={!dirty || !isValid}>
              {isNewMode ? "Create source" : "Save changes"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={!dirty}
              onClick={() => resetForm({ values: initialValues })}
            >
              Cancel
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
